import { useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import { useColorScheme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Footer from './Footer';
import Header from './Header';
import Sidebar from './Sidebar';
import HorizontalBar from './HorizontalBar';
import MainContentStyled from './MainContentStyled';
// import Customization from '../Customization';
import Loader from 'ui-component/Loader';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import useUserPreferences from 'hooks/useUserPreferences';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));

  const {
    state: { borderRadius, container, miniDrawer, menuOrientation },
    setField
  } = useConfig();
  const { setMode } = useColorScheme();
  // MUI's dev build hands out a new setMode whenever the mode changes; read it through a ref so the
  // effect below only fires when the saved preference changes.
  const setModeRef = useRef(setMode);
  setModeRef.current = setMode;
  const { menuMaster, menuMasterLoading } = useGetMenuMaster();
  const { userPreferences, userPreferencesLoading, userPreferencesLoaded } = useUserPreferences();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;

  useEffect(() => {
    handlerDrawerOpen(!miniDrawer);
  }, [miniDrawer]);

  useEffect(() => {
    downMD && handlerDrawerOpen(false);
  }, [downMD]);

  useEffect(() => {
    if (!downMD && !userPreferencesLoading) handlerDrawerOpen(!userPreferences.isSideMenuMinimised);
  }, [downMD, userPreferences.isSideMenuMinimised, userPreferencesLoading]);

  // The saved account preferences own the menu layout and colour scheme; mirror them into the
  // config / MUI colour-scheme state the rest of the layout reads from. Only real saved values are
  // applied: while the record is missing (e.g. refetching after a tenant switch) the current layout
  // and scheme are kept rather than flipping to the defaults.
  useEffect(() => {
    if (userPreferencesLoaded && userPreferences.menuOrientation !== menuOrientation)
      setField('menuOrientation', userPreferences.menuOrientation);
  }, [userPreferences.menuOrientation, userPreferencesLoaded, menuOrientation, setField]);

  // Apply the preference when it loads or changes, never on every mode change: MUI syncs the mode across
  // tabs through localStorage, and re-asserting here made two tabs with different preferences fight
  // (each flipping the other back), flashing between light and dark.
  useEffect(() => {
    if (userPreferencesLoaded) setModeRef.current(userPreferences.themeMode);
  }, [userPreferences.themeMode, userPreferencesLoaded]);

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downMD;

  // horizontal menu-list bar : drawer
  const menu = useMemo(() => (isHorizontal ? <HorizontalBar /> : <Sidebar />), [isHorizontal]);

  if (menuMasterLoading) return <Loader />;

  return (
    <Box sx={{ display: 'flex' }}>
      {/* header */}
      <AppBar enableColorOnDark position="fixed" color="inherit" elevation={0} sx={{ bgcolor: 'background.default' }}>
        <Toolbar sx={isHorizontal ? { p: 1.25, pb: 0, minHeight: { xs: 61, sm: 61 } } : { p: 2 }}>
          <Header />
        </Toolbar>
      </AppBar>

      {/* menu / drawer */}
      {menu}

      {/* main content */}
      <MainContentStyled {...{ borderRadius, menuOrientation, open: drawerOpen }}>
        <Container
          maxWidth={container ? 'lg' : false}
          sx={{ ...(!container && { px: { xs: 0 } }), minHeight: 'calc(100vh - 128px)', display: 'flex', flexDirection: 'column' }}
        >
          {/* breadcrumb */}
          <Breadcrumbs />
          <Outlet />
          <Footer />
        </Container>
      </MainContentStyled>
      {/* <Customization /> */}
    </Box>
  );
}
